import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../../api';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const FacilitiesSection = () => {
  const { miniSlug, slug } = useParams();
  const [facilitiesData, setFacilitiesData] = useState({
    title: 'Facilities',
    items: [],
    loading: true
  });

  // Modal states
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  const PrevArrow = ({ onClick }) => (
    <button
      type="button"
      className="slick-prev"
      onClick={onClick}
      aria-label="Previous facility slide"
    >
      Previous
    </button>
  );

  const NextArrow = ({ onClick }) => (
    <button
      type="button"
      className="slick-next"
      onClick={onClick}
      aria-label="Next facility slide"
    >
      Next
    </button>
  );

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: !isPaused,
    autoplaySpeed: 5000,
    arrows: true,
    fade: true,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    accessibility: true
  };

  useEffect(() => {
    const fetchFacilitiesData = async () => {
      try {
        const response = await api.get(
          `${process.env.REACT_APP_API_URL}/api/${miniSlug}/${slug}/facilities`
        );
        const data = response.data;
        setFacilitiesData({
          title: data.title || 'Facilities',
          items: data.items || [],
          loading: false
        });
      } catch (error) {
        console.error('Error fetching facilities data:', error);
        setFacilitiesData({
          title: 'Facilities',
          items: [],
          loading: false
        });
      }
    };

    fetchFacilitiesData();
  }, [miniSlug, slug]);

  const CHARACTER_LIMIT = 500;

  if (facilitiesData.loading || facilitiesData.items.length === 0) {
    return <></>;
  }

  return (
    <section className="highlighted_news_section" id="facilities">
      <div className="container-fluid px-0">
        <h2 className="theme-heading facility_heading">{facilitiesData.title}</h2>

        {facilitiesData.items.length > 0 && (
          <div
            role="region"
            aria-roledescription="carousel"
            aria-label="Facilities Carousel"
            className="carousel-wrapper"
          >
            <div className="carousel-controls text-end mb-2">
              <button
                type="button"
                onClick={() => setIsPaused(!isPaused)}
                aria-label={isPaused ? "Start carousel autoplay" : "Pause carousel autoplay"}
                className="carousel-pause-btn"
              >
                {isPaused ? "Play" : "Pause"}
              </button>
            </div>
            <Slider {...settings} className="highlighted_news_carousel">
            {facilitiesData.items.map((facility, index) => {
              const text = facility.description || '';
              const truncatedText =
                text.length > CHARACTER_LIMIT ? text.slice(0, CHARACTER_LIMIT) + '...' : text;

              return (
                <div
                  className="news_slide position-relative px-5"
                  key={index}
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`${index + 1} of ${facilitiesData.items.length}`}
                >
                  <div className="news_image position-relative">
                    {facility.video_link ? (
                      <a
                        href={facility.video_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="video-link-wrapper"
                      >
                        <img
                          src={`${process.env.REACT_APP_API_URL}/public${facility.image}`}
                          alt={facility.title}
                          className="img-fluid"
                        />
                        <div className="play-icon-overlay">
                          <i className="play-icon fa fa-play" />
                        </div>
                      </a>
                    ) : (
                      <img
                        src={`${process.env.REACT_APP_API_URL}/public${facility.image}`}
                        alt={facility.title}
                        className="img-fluid"
                      />
                    )}
                  </div>
                  <div className="news_content_card">
                    <h3 className="news_title ">{facility.title}</h3>
                    <p className="news_excerpt " >
                      {truncatedText}{' '}
                      {text.length > CHARACTER_LIMIT && (
                        <button
                          type="button"
                          className="view-more-link"
                          onClick={() => setSelectedFacility(facility)}
                          aria-expanded={selectedFacility?.title === facility.title}
                          aria-controls={`facility-modal-${index}`}
                        >
                          View More
                        </button>
                      )}
                    </p>
                  </div>
                </div>
              );
            })}
            </Slider>
          </div>
        )}
      </div>

      {/* Modal Section */}
      {selectedFacility && (
        <div className="facility-modal-overlay" onClick={() => setSelectedFacility(null)}>
          <div
            id={`facility-modal-${facilitiesData.items.findIndex(
              (item) => item.title === selectedFacility.title
            )}`}
            className="facility-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="facility-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="modal-close"
              onClick={() => setSelectedFacility(null)}
              aria-label="Close facility details"
            >
              &times;
            </button>
            <h3 id="facility-modal-title">
              {selectedFacility.title}
            </h3>
            <div className="row align-items-center">
              <div className="col-lg-6">
                <div className="modal-image-wrapper">
                  <img src={`${process.env.REACT_APP_API_URL}/public${selectedFacility.image}`} alt={selectedFacility.title} className="modal-image" />
                </div>
              </div>
              <div className="col-lg-6 modal-text">
                <p>{selectedFacility.description}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default FacilitiesSection;
